import React, { useEffect, useState } from 'react'
import { Button, Flex, Input, SelectDropdown } from '../../lib/components'
import { DeleteIcon } from '../../lib/components/Icon'
import {
  CUSTOM_KEY_OPTIONS,
  BOOLEAN_CUSTOM_FIELD_OPTIONS,
  hasConfiguredFieldValue,
} from '../../lib/utils/fieldMapping'

type MappingRow = {
  id: string
  keyName: string
  value: string
}

interface CustomFieldEditorProps {
  existingTerms?: Record<string, any>
  onChange: (updatedFields: Record<string, any>) => void
}

const CustomFieldEditor: React.FC<CustomFieldEditorProps> = ({ existingTerms, onChange }) => {
  const [rows, setRows] = useState<MappingRow[]>([])

  useEffect(() => {
    if (existingTerms) {
      const excludedKeys = ['id', 'created_at', 'updated_at', 'investor_id', 'opportunity_id']
      const initialRows: MappingRow[] = Object.entries(existingTerms)
        .filter(([key, val]) => !excludedKeys.includes(key) && hasConfiguredFieldValue(val))
        .map(([key, val]) => ({
          id: crypto.randomUUID(),
          keyName: key,
          value: val?.toString?.() ?? '',
        }))
      setRows(initialRows)
      onChange(
        initialRows.reduce((acc, r) => {
          if (r.keyName && hasConfiguredFieldValue(r.value)) acc[r.keyName] = r.value
          return acc
        }, {} as Record<string, any>),
      )
    }
  }, [existingTerms])

  const rowsToObject = (r: MappingRow[]) =>
    r.reduce((acc, row) => {
      if (row.keyName && hasConfiguredFieldValue(row.value)) acc[row.keyName] = row.value
      return acc
    }, {} as Record<string, any>)

  const handleKeyChange = (id: string, newKey: string) => {
    setRows((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, keyName: newKey } : r))
      onChange(rowsToObject(updated))
      return updated
    })
  }

  const handleValueChange = (id: string, newValue: string) => {
    setRows((prev) => {
      const updated = prev.map((r) => (r.id === id ? { ...r, value: newValue } : r))
      onChange(rowsToObject(updated))
      return updated
    })
  }

  const handleAddField = () => {
    const newRow: MappingRow = { id: crypto.randomUUID(), keyName: '', value: '' }
    const updated = [...rows, newRow]
    setRows(updated)
    onChange(rowsToObject(updated))
  }

  const handleRemoveField = (id: string) => {
    const updated = rows.filter((r) => r.id !== id)
    setRows(updated)
    onChange(rowsToObject(updated))
  }

  return (
    <div className='field-container mt-24'>
      {rows.length === 0 && (
        <div className='text-gray-500 text-sm mb-16'>No custom fields yet. Add one below.</div>
      )}

      {rows.map((row, idx) => (
        <Flex key={row.id} variant='space-between' classes={['mb-24', 'gap-12']}>
          <div className='field-container' style={{ flex: 1, minWidth: 0 }}>
            <div className='input-label mb-8'>Field Key</div>
            <SelectDropdown
              name={`custom_key_${idx}`}
              list={CUSTOM_KEY_OPTIONS}
              value={row.keyName}
              handleChange={(val: any) => handleKeyChange(row.id, val)}
            />
          </div>

          <div className='field-container' style={{ flex: 1, minWidth: 0 }}>
            <div className='input-label mb-8'>Value</div>
            {row.keyName === 'reinvestment_option_enabled' ? (
              <SelectDropdown
                name={`custom_value_${idx}`}
                list={BOOLEAN_CUSTOM_FIELD_OPTIONS}
                value={row.value}
                handleChange={(val: string) => handleValueChange(row.id, val)}
                dataTestId={`profile-detail-custom-field-${row.keyName}`}
              />
            ) : (
              <Input
                name={`custom_value_${idx}`}
                type='text'
                placeholder='Enter value'
                value={row.value}
                handleChange={(e) => handleValueChange(row.id, e.target.value)}
              />
            )}
          </div>

          <div className='mt-36' style={{ flexShrink: 0 }}>
            <Button
              classes={['auto-btn-width']}
              variant='secondary-btn'
              IconComponent={<DeleteIcon classes={['button-icon']} />}
              handleClick={() => handleRemoveField(row.id)}
            />
          </div>
        </Flex>
      ))}

      <Button
        classes={['mt-24']}
        variant='primary-btn'
        text='+ Add Field'
        handleClick={handleAddField}
      />
    </div>
  )
}

export default CustomFieldEditor
